import { hasReticulumServer } from "./phoenix-utils";
import configs from "./configs";
import { resolveShortlinkTarget } from "./shortlink-url-utils";

const nonCorsProxyDomains = (configs.NON_CORS_PROXY_DOMAINS || "").split(",");
if (configs.CORS_PROXY_SERVER) {
  nonCorsProxyDomains.push(configs.CORS_PROXY_SERVER.split(":")[0]);
}
nonCorsProxyDomains.push(document.location.hostname);
const commonKnownContentTypes = {
  gltf: "model/gltf",
  glb: "model/gltf-binary",
  gif: "image/gif",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  pdf: "application/pdf",
  mp4: "video/mp4",
  mp3: "audio/mpeg",
  basis: "image/basis",
  ktx2: "image/ktx2",
  m3u8: "application/vnd.apple.mpegurl",
  mpd: "application/dash+xml"
};

// thanks to https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
function b64EncodeUnicode(str) {
  // first we use encodeURIComponent to get percent-encoded UTF-8, then we convert the percent-encodings
  // into raw bytes which can be fed into btoa.
  const CHAR_RE = /%([0-9A-F]{2})/g;
  return btoa(encodeURIComponent(str).replace(CHAR_RE, (_, p1) => String.fromCharCode("0x" + p1)));
}

const farsparkEncodeUrl = url => {
  // farspark doesn't know how to read '=' base64 padding characters
  // translate base64 + to - and / to _ for URL safety
  return b64EncodeUnicode(url).replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");
};

// Interceptor to replace nextrealitydigital URLs with mudfoundation URLs
const replaceBlobStorageUrl = url => {
  if (typeof url === "string" && url.includes("https://nextrealitydigital.blob.core.windows.net/")) {
    return url.replace(
      /https:\/\/nextrealitydigital\.blob\.core\.windows\.net\//g,
      "https://mudfoundation.blob.core.windows.net/"
    );
  }
  return url;
};

export const scaledThumbnailUrlFor = (url, width, height) => {
  // Apply URL replacement interceptor
  url = replaceBlobStorageUrl(url);

  let extension = "";
  try {
    const pathParts = new URL(url).pathname.split(".");

    if (pathParts.length > 1) {
      const extensionCandidate = pathParts.pop();
      if (commonKnownContentTypes[extensionCandidate]) {
        extension = `.${extensionCandidate}`;
      }
    }
  } catch (e) {
    extension = ".png";
  }

  // HACK: the extension is needed to ensure CDN caching on Cloudflare
  const thumbnailUrl = `https://${configs.THUMBNAIL_SERVER}/thumbnail/${farsparkEncodeUrl(
    url
  )}${extension}?w=${width}&h=${height}`;

  try {
    const urlHostname = new URL(url).hostname;

    if (hasReticulumServer()) {
      const retHostname = new URL(`https://${configs.RETICULUM_SERVER}`).hostname;
      if (retHostname === urlHostname) return url;
    }
  } catch (e) {
    return thumbnailUrl;
  }

  return thumbnailUrl;
};

const LOCAL_ASSET_HOST_PATTERN = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$|\.local$/i;

export const isLocalAssetUrl = url => {
  if (typeof url !== "string") return false;

  try {
    return LOCAL_ASSET_HOST_PATTERN.test(new URL(url).hostname);
  } catch (e) {
    return false;
  }
};

export const avatarThumbnailUrlFor = (url, width, height) =>
  isLocalAssetUrl(url) ? url : scaledThumbnailUrlFor(url, width, height);

export const isNonCorsProxyDomain = hostname => {
  return nonCorsProxyDomains.find(domain => hostname.endsWith(domain));
};

export const proxyUrlShortenerMS = `https://${configs.CORS_PROXY_SERVER}`;

export const proxyUrl =
  configs.ENVIRONMENT && configs.ENVIRONMENT == "local" ? "" : `https://${configs.CORS_PROXY_SERVER}`;

export const proxiedUrlFor = url => {
  if (typeof url !== "string") return url;

  // Apply URL replacement interceptor
  url = replaceBlobStorageUrl(url);

  if (!(url.startsWith("http:") || url.startsWith("https:"))) return url;

  // Skip known domains that do not require CORS proxying.
  try {
    const parsedUrl = new URL(url);
    if (isNonCorsProxyDomain(parsedUrl.hostname)) return url;
  } catch (e) {
    // Ignore
  }

  return `https://${configs.CORS_PROXY_SERVER}/${url}`;
};

export const proxiedUrlForArtwork = url => {
  // if(url.includes('api.nga.gov')) {
  //   return url;
  // }

  return proxiedUrlFor(url);
};

export function getAbsoluteUrl(baseUrl, relativeUrl) {
  return new URL(relativeUrl, baseUrl);
}

export function getAbsoluteHref(baseUrl, relativeUrl) {
  return getAbsoluteUrl(baseUrl, relativeUrl).href;
}

// Note these files are configured in webpack.config.js to be handled with file-loader, so this will be a string containing the file paths
export const rewriteBasisTranscoderUrls = function (url) {
  return url;
};

export const getCustomGLTFParserURLResolver = gltfUrl => url => {
  if (typeof url !== "string" || url === "") return "";

  // Apply URL replacement interceptor to both gltfUrl and relative url
  gltfUrl = replaceBlobStorageUrl(gltfUrl);
  url = replaceBlobStorageUrl(url);

  if (/^(https?:)?\/\//i.test(url)) return proxiedUrlFor(url);
  if (/^data:.*,.*$/i.test(url)) return url;
  if (/^blob:.*$/i.test(url)) return url;

  if (configs.CORS_PROXY_SERVER) {
    // For absolute paths with a CORS proxied gltf URL, re-write the url properly to be proxied
    const corsProxyPrefix = `https://${configs.CORS_PROXY_SERVER}/`;

    if (gltfUrl.startsWith(corsProxyPrefix)) {
      const originalUrl = decodeURIComponent(gltfUrl.substring(corsProxyPrefix.length));
      const originalUrlParts = originalUrl.split("/");

      // Drop the .gltf filename
      const path = new URL(url).pathname;
      const assetUrl = originalUrlParts.slice(0, originalUrlParts.length - 1).join("/") + "/" + path;
      return corsProxyPrefix + assetUrl;
    }
  }

  return url;
};

const dataUrlRegex = /data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/;
const hubsVideoUrlRegex = /^hubs:\/\/clients\/[^/]+\/video(?:[?#].*)?$/;

export const guessContentType = url => {
  if (!url) return;
  if (hubsVideoUrlRegex.test(url)) return "video/vnd.hubs-webrtc";
  if (url.startsWith("data:")) {
    const matches = dataUrlRegex.exec(url);
    if (matches.length > 0) {
      matches[1];
    }
  }
  let pathname;
  try {
    pathname = new URL(url, window.location).pathname;
  } catch (e) {
    pathname = String(url).split(/[#?]/)[0];
  }

  const extension = pathname.split(".").pop();
  return commonKnownContentTypes[extension];
};

export const getMediaTypeFromContentType = contentType => {
  if (!contentType) return null;

  const normalizedContentType = contentType.split(";")[0].trim().toLowerCase();

  if (normalizedContentType.includes("gif")) return "gif";
  if (normalizedContentType.startsWith("image/")) return "image";
  if (normalizedContentType.startsWith("video/")) return "video";
  if (normalizedContentType.startsWith("audio/")) return "audio";
  if (normalizedContentType.startsWith("application/pdf")) return "pdf";
  if (
    normalizedContentType === "application/octet-stream" ||
    normalizedContentType.includes("glb") ||
    normalizedContentType.startsWith("model/gltf")
  ) {
    return "model";
  }

  return null;
};

export const getMediaTypeFromUploadEntry = (uploadEntry, fallbackUrl) => {
  const fileObject = uploadEntry?.fileObject || uploadEntry;
  const source =
    uploadEntry?.downloableUrl ||
    uploadEntry?.downloadableUrl ||
    fileObject?.downloableUrl ||
    fileObject?.downloadableUrl ||
    fileObject?.name ||
    uploadEntry?.name ||
    fallbackUrl;

  const mediaTypeFromSource = getMediaTypeFromContentType(guessContentType(source));
  if (mediaTypeFromSource) return mediaTypeFromSource;

  return getMediaTypeFromContentType(fileObject?.type || uploadEntry?.type);
};

const originIsHubsServer = new Map();
originIsHubsServer.set(document.location.origin, true);
originIsHubsServer.set("https://" + configs.CORS_PROXY_SERVER, false);

async function isHubsServer(url) {
  if (!url) return false;
  if (!url.startsWith("http")) {
    url = "https://" + url;
  }
  const { origin } = new URL(url);

  if (originIsHubsServer.has(origin)) {
    return originIsHubsServer.get(origin);
  }

  let isHubsServer;
  try {
    isHubsServer = (await fetch(proxiedUrlFor(origin), { method: "HEAD" })).headers.has("hub-name");
  } catch (e) {
    isHubsServer = false;
  }
  originIsHubsServer.set(origin, isHubsServer);
  return isHubsServer;
}

const hubsSceneRegex = /https?:\/\/[^/]+\/scenes\/[a-zA-Z0-9]{7}(?:\/|$)/;
const hubsAvatarRegex = /https?:\/\/[^/]+\/avatars\/(?<id>[a-zA-Z0-9]{7})(?:\/|$)/;
const hubsRoomRegex = /(https?:\/\/)?[^/]+\/(?<id>[a-zA-Z0-9]{7})(?:\/|$)/;
const persistenceAvatarIdPattern = /^[^/.?#/]+$/;

export const idForPersistenceAvatarUrl = (url, { persistenceApiUrl = configs.PERSISTENCE_API_URL } = {}) => {
  if (!url || !persistenceApiUrl) return null;

  let parsedUrl;
  let persistenceOrigin;
  try {
    const persistenceUrl = new URL(persistenceApiUrl);
    persistenceOrigin = persistenceUrl.origin;
    parsedUrl = new URL(url, persistenceOrigin);
  } catch (e) {
    return null;
  }

  if (parsedUrl.origin !== persistenceOrigin) return null;

  const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
  const avatarId =
    pathParts[0] === "avatars"
      ? pathParts[1]
      : pathParts[0] === "workspaces" && pathParts[2] === "avatars"
      ? pathParts[3]
      : null;

  return avatarId && persistenceAvatarIdPattern.test(avatarId) ? avatarId : null;
};

export const isPersistenceAvatarUrl = url => !!idForPersistenceAvatarUrl(url);

export const isLocalHubsUrl = async url =>
  (await isHubsServer(url)) &&
  (new URL(url).origin === document.location.origin || document.location.origin.startsWith("https://hubs.local"));

export const isHubsSceneUrl = async url => (await isHubsServer(url)) && hubsSceneRegex.test(url);
export const isLocalHubsSceneUrl = async url => (await isHubsSceneUrl(url)) && (await isLocalHubsUrl(url));

export const isHubsAvatarUrl = async url => (await isHubsServer(url)) && hubsAvatarRegex.test(url);
export const isLocalHubsAvatarUrl = async url => (await isHubsAvatarUrl(url)) && (await isLocalHubsUrl(url));
export const isLocalOrPersistenceAvatarUrl = async url =>
  isPersistenceAvatarUrl(url) || (await isLocalHubsAvatarUrl(url));

const isDirectHubsRoomUrl = async url =>
  (await isHubsServer(url)) &&
  !(await isHubsAvatarUrl(url)) &&
  !(await isHubsSceneUrl(url)) &&
  url.match(hubsRoomRegex)?.groups.id;

export const isHubsRoomUrl = async url => {
  const directRoomId = await isDirectHubsRoomUrl(url);
  if (directRoomId) {
    return directRoomId;
  }

  const resolvedShortlinkUrl = await resolveShortlinkTarget(url, {
    shortlinkDomain: configs.SHORTLINK_DOMAIN,
    corsProxyServer: configs.CORS_PROXY_SERVER
  });

  if (!resolvedShortlinkUrl) {
    return null;
  }

  return isDirectHubsRoomUrl(resolvedShortlinkUrl);
};

export const isHubsDestinationUrl = async url =>
  (await isHubsServer(url)) && ((await isHubsSceneUrl(url)) || (await isHubsRoomUrl(url)));

export const idForAvatarUrl = url => {
  const persistenceAvatarId = idForPersistenceAvatarUrl(url);
  if (persistenceAvatarId) return persistenceAvatarId;

  const match = url.match(hubsAvatarRegex);
  if (match) {
    return match.groups.id;
  }
  return null;
};
